import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { ExternalLink } from "lucide-react";
import { INTEL_API, errMsg } from "./OutreachSendButton";

// File « À valider par Gilles » (v2.1.6.4.1) — alimentée par l'arbitre éditorial : tâches de revue humaine PENDING.
// Contrat API : GET /count · GET /tasks?status=PENDING · GET /tasks/{id} · POST /tasks/{id}/resolve {resolution: APPROVED|CORRECTED|REJECTED, note}
// Résoudre ne publie JAMAIS : cela lève seulement l'interdit de publication ; la publication reste une action humaine explicite de la file éditoriale.
const HR = `${INTEL_API}/editorial/human-review`;
const RESOLUTIONS = [["APPROVED", "Valider tel quel"], ["CORRECTED", "Corrigé dans l'éditeur"], ["REJECTED", "Rejeter cette version"]];

const TaskDialog = ({ taskId, onClose, onResolved }) => {
  const [t, setT] = useState(null);
  const [note, setNote] = useState("");
  useEffect(() => { axios.get(`${HR}/tasks/${taskId}`).then((r) => setT(r.data)).catch((e) => toast.error(errMsg(e))); }, [taskId]);
  const resolve = async (resolution) => {
    try { await axios.post(`${HR}/tasks/${taskId}/resolve`, { resolution, note: note || null }); toast.success("Revue enregistrée"); onResolved(); onClose(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="intel-review-dialog">
        <DialogHeader><DialogTitle>{t ? t.title : "Revue humaine"}</DialogTitle></DialogHeader>
        {t && (
          <div className="space-y-3 text-sm">
            <p className="text-xs text-muted-foreground">Arbitre : {t.arbiter_decision} · fact-check : {t.factcheck_verdict} · créé {t.created_at?.slice(0, 16)}</p>
            {t.reason && <p>{t.reason}</p>}
            <div>
              <p className="font-semibold">Problèmes matériels ({t.material_issues?.length || 0})</p>
              {(t.material_issues || []).map((m, i) => <div key={i} className="border-l-2 border-red-400 pl-2 my-1"><p>{m.summary}</p><p className="text-xs text-muted-foreground">{m.location} — {m.why_material}</p></div>)}
              {!(t.material_issues || []).length && <p className="text-xs text-muted-foreground">Aucun (arbitrage indisponible : revue demandée par précaution).</p>}
            </div>
            {(t.minor_edits || []).length > 0 && <div><p className="font-semibold">Retouches mineures</p>{t.minor_edits.map((m, i) => <p key={i} className="text-xs">• {m.summary} <span className="text-muted-foreground">({m.location})</span></p>)}</div>}
            {t.status === "PENDING" ? (
              <div className="space-y-2 border-t pt-2">
                <Input placeholder="Note (facultatif)" value={note} onChange={(e) => setNote(e.target.value)} data-testid="intel-review-note" />
                <div className="flex flex-wrap gap-2">{RESOLUTIONS.map(([r, l]) => <Button key={r} size="sm" variant={r === "REJECTED" ? "outline" : "default"} onClick={() => resolve(r)} data-testid={`intel-review-resolve-${r}`}>{l}</Button>)}</div>
                <p className="text-xs text-muted-foreground">Résoudre ne publie rien : la publication reste une action explicite de la file éditoriale.</p>
              </div>
            ) : <p className="text-xs">Résolu : {t.resolution} par {t.reviewed_by} le {t.reviewed_at?.slice(0, 16)}</p>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export const HumanReviewQueue = ({ onChange }) => {
  const [data, setData] = useState({ pending: 0, tasks: [] });
  const [open, setOpen] = useState(null);
  const load = useCallback(async () => { try { setData((await axios.get(`${HR}/tasks`, { params: { status: "PENDING" } })).data); } catch (e) { toast.error(errMsg(e)); } }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="space-y-3" data-testid="intel-human-review-queue">
      <h2 className="text-lg font-semibold" data-testid="intel-human-review-count">{data.pending} article{data.pending > 1 ? "s" : ""} à valider</h2>
      {data.tasks.length === 0 && <p className="text-sm text-muted-foreground">Aucune validation en attente.</p>}
      {data.tasks.map((t) => (
        <div key={t.id} className="border border-border rounded-lg p-3 bg-white flex flex-wrap items-center gap-3" data-testid={`intel-review-task-${t.id}`}>
          <Badge className="bg-red-100 text-red-900">Validation humaine requise</Badge>
          <span className="font-semibold">{t.title}</span>
          <span className="text-xs text-muted-foreground">{t.created_at?.slice(0, 10)} · {t.material_issues_count} problème(s) matériel(s) · arbitre : {t.arbiter_decision}</span>
          {t.reason && <span className="text-xs max-w-xl truncate" title={t.reason}>{t.reason}</span>}
          <a href={`/intelligence/preview/${t.article_id}`} target="_blank" rel="noreferrer" className="text-sm underline flex items-center gap-1"><ExternalLink className="w-3 h-3" />Article</a>
          <Button size="sm" onClick={() => setOpen(t.id)} data-testid={`intel-review-open-${t.id}`}>Examiner</Button>
        </div>
      ))}
      {open && <TaskDialog taskId={open} onClose={() => setOpen(null)} onResolved={() => { load(); onChange?.(); }} />}
    </div>
  );
};
